import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/buttons/button";
import { NumberCard } from "../../../components/cards/numberCard";
import { ProfileIcon } from "../../../components/cards/profilePic";
import { SearchInput } from "../../../components/inputs/searchInput";
import { Pill } from "../../../components/pill";
import { TableComponent } from "../../../components/table";
import { TabNav } from "../../../components/tabNav";
import { PageHeader } from "../../../components/typography/pageHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useGetAllUsersQuery } from "../../../redux/features/admin/users/users.api";
import { GetAllUsersQuery } from "../../../redux/features/admin/users/users.types";
import { userStatusPillMap } from "../../../utils/constants";

export const UsersPage = () => {
  const [query, setQuery] = useState<GetAllUsersQuery>({
    page: 1,
    limit: 20,
    search: undefined,
    sortOrder: "asc",
    status: undefined,
    userType: undefined,
  });

  const navigate = useNavigate();

  const tabs = [
    {
      title: "All",
      to: "all",
      onClick: () => {
        setQuery({
          ...query,
          page: 1,
          status: undefined,
        });
      },
    },
    {
      title: "Active",
      to: "active",
      onClick: () => {
        setQuery({
          ...query,
          page: 1,
          status: "active",
        });
      },
    },
    {
      title: "Suspended",
      to: "suspended",
      onClick: () => {
        setQuery({
          ...query,
          page: 1,
          status: "suspended",
        });
      },
    },
    {
      title: "Deactivated",
      to: "inactive",
      onClick: () => {
        setQuery({
          ...query,
          page: 1,
          status: "inactive",
        });
      },
    },
  ];

  const {
    isFetching: fetchingUsers,
    isLoading: loadingUsers,
    data: usersData,
  } = useGetAllUsersQuery(
    { ...query, search: query.search === "" ? undefined : query.search },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  return (
    <div className="w-full">
      <PageHeader header={"Users"} className="font-unbounded font-normal" />

      <TabNav
        navs={tabs}
        className="mt-8 !min-w-full md:!min-w-fit md:!max-w-fit"
        tabClassName=""
        activeTab={query.status ?? "all"}
        variant="switcher"
        theme="plain"
      />

      <div className="mt-3 flex flex-col gap-[2px]">
        <BgWrapper className="grid grid-cols-1 gap-[10px] rounded-b-none md:grid-cols-3">
          <NumberCard
            value={usersData?.data?.stats?.total ?? 0}
            text="Total Users"
            isCurrency={false}
            loading={loadingUsers}
            titleFirst
          />

          <NumberCard
            value={usersData?.data?.stats?.guest ?? 0}
            text="Total Guests"
            isCurrency={false}
            loading={loadingUsers}
            titleFirst
          />

          <NumberCard
            value={usersData?.data?.stats?.organiser ?? 0}
            text="Total Organizers"
            isCurrency={false}
            loading={loadingUsers}
            titleFirst
          />
        </BgWrapper>

        <BgWrapper className="rounded-t-none">
          <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
            <SearchInput
              searchFunction={(value) => {
                setQuery({
                  ...query,
                  search: value,
                  page: 1,
                });
              }}
              placeholder="Search"
              className="md:!max-w-[300px]"
            />

            <div className="grid w-full grid-cols-2 items-center gap-3 md:flex md:w-fit md:flex-row">
              <Button
                type="button"
                onClick={() => {
                  if (query.sortOrder === "asc") {
                    setQuery({ ...query, sortOrder: "desc" });
                  } else {
                    setQuery({ ...query, sortOrder: "asc" });
                  }
                }}
                text="Date Modified"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon={query.sortOrder === "asc" ? "order-asc" : "order-desc"}
                variation="grey"
              />

              <Button
                type="button"
                onClick={() => {}}
                text="Export"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon="export"
                variation="grey"
              />
            </div>
          </div>

          <TableComponent
            headers={[]}
            rows={
              usersData?.data?.users?.map((user, idx) => {
                return {
                  id: idx,
                  items: [
                    <div
                      className={`flex w-full flex-1 flex-col gap-2 md:flex-row md:items-center`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <ProfileIcon img="" dimension={32} className="" />
                      </div>

                      <p className="flex flex-1 flex-wrap items-center gap-0 text-sm text-grey-600">
                        <span className="font-bold text-grey-900">
                          {user.name}
                        </span>
                        <span className={` `}>∙</span>
                        <span className={``}>{user.email}</span>
                      </p>

                      <div className="flex flex-row flex-wrap items-center gap-2">
                        <Pill
                          text={
                            user.userType === "attendee" ? "GUEST" : "ORGANIZER"
                          }
                          variation={
                            user.userType === "attendee" ? "blue" : "purple"
                          }
                        />
                        {user.isOrganisation && (
                          <Pill text={"COMPANY"} variation={"grey"} />
                        )}
                        {user.isOrganisation && (
                          <Pill text={"UNVERIFIED SA"} variation={"grey"} />
                        )}
                        <Pill
                          text={userStatusPillMap[user.status]?.text}
                          variation={userStatusPillMap[user.status]?.variant}
                        />
                      </div>
                    </div>,
                  ],
                };
              }) ?? []
            }
            loadingRows={query.limit}
            isLoading={fetchingUsers}
            isRowClickable
            onRowClick={(idx) => {
              navigate(`/users/${usersData?.data?.users[idx]?._id}`);
            }}
            isTableEmpty={[0, undefined].includes(
              usersData?.data?.users?.length,
            )}
            emptyTitle="No Users"
            emptySubTitle=""
            emptyImage="/assets/images/guest-glass.png"
            isPaginated
            currentPage={query.page ?? 1}
            totalCount={usersData?.data?.totalCount ?? 0}
            itemsPerPage={query.limit!}
            pageRangeDisplayed={3}
            onPageChange={(page) => {
              setQuery({
                ...query,
                page: page,
              });
            }}
            className="mt-3"
          />
        </BgWrapper>
      </div>
    </div>
  );
};
